/**
 * Breach Cost Calculator Engine
 * Calculates total data breach costs using IBM methodology
 */

import {
  BreachOrganizationProfile,
  BreachDataProfile,
  BreachSecurityPosture,
  BreachCostResult,
  PreventionRecommendation,
  DataType
} from '@/types/calculator';

import {
  IBM_GLOBAL_AVERAGES,
  INDUSTRY_MULTIPLIERS,
  REGION_MULTIPLIERS,
  getScaleFactor,
  MATURITY_MULTIPLIERS,
  DETECTION_COSTS,
  NOTIFICATION_COSTS,
  POST_BREACH_COSTS,
  MITIGATION_FACTORS,
  calculateGDPRFine,
  calculateHIPAAFine,
  calculatePCIDSSFine,
  calculateStateFine,
  calculateFinancialFine,
  getDisruptionPercentage,
  PREVENTION_EFFECTIVENESS,
  INDUSTRY_BREACH_COSTS
} from './breach-cost-constants';

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

// ============================================================================
// MAIN CALCULATION FUNCTION
// ============================================================================

export function calculateBreachCost(
  org: BreachOrganizationProfile,
  data: BreachDataProfile,
  posture: BreachSecurityPosture
): BreachCostResult {
  // Step 1: Calculate cost per record
  const costPerRecord = calculateCostPerRecord(
    org.industry,
    org.region,
    data.recordCount
  );

  // Step 2: Base record cost
  const baseRecordCost = costPerRecord * data.recordCount;

  // Step 3: Fixed costs
  const detectionCost = calculateDetectionCosts(posture.mttd);
  const notificationCost = calculateNotificationCosts(
    data.recordCount,
    data.regulatedDataPercent
  );
  const legalCost = calculateLegalCosts(
    org.industry,
    data.recordCount,
    data.dataTypes
  );
  const regulatoryFines = calculateRegulatoryFines(
    org.industry,
    data.recordCount,
    data.dataTypes,
    org.region,
    org.annualRevenue || 0
  );
  const businessDisruption = calculateBusinessDisruption(
    org.annualRevenue || 0,
    data.recordCount,
    org.industry,
    posture.mttc
  );
  const postBreachCost = calculatePostBreachCosts();

  // Step 4: Subtotal
  const subtotal =
    baseRecordCost +
    detectionCost +
    notificationCost +
    legalCost +
    regulatoryFines +
    businessDisruption.total +
    postBreachCost;

  // Step 5: Apply maturity multiplier
  const maturityMult = MATURITY_MULTIPLIERS[posture.maturityLevel];
  const afterMaturity = subtotal * maturityMult;

  // Step 6: Apply mitigation factors
  const mitigationFactor = calculateMitigationFactor(posture);
  const totalCost = afterMaturity * mitigationFactor;

  const baselinePosture: BreachSecurityPosture = {
    ...posture,
    maturityLevel: 'minimal',
    hasIRPlan: false,
    irPlanTested: false,
    aiUsage: 'none',
    mttd: Math.max(posture.mttd, 220),
    mttc: Math.max(posture.mttc, 120)
  };
  const baselineMitigation = calculateMitigationFactor(baselinePosture);
  const costWithoutControls = subtotal * MATURITY_MULTIPLIERS.minimal * baselineMitigation;

  // Step 7: Insurance analysis
  const insuranceCoverage = posture.hasCyberInsurance
    ? totalCost * 0.65  // 65% average coverage
    : 0;
  const outOfPocket = totalCost - insuranceCoverage;
  const premiumIncrease = 35000 * 3; // 3-year increase

  // Step 8: Industry comparison
  const industryAverage = INDUSTRY_BREACH_COSTS[org.industry];
  const globalAverage = IBM_GLOBAL_AVERAGES.totalCost;
  const percentDifference = ((totalCost - industryAverage) / industryAverage) * 100;

  // Generate comparison text
  const comparisonText = totalCost > industryAverage
    ? `Your estimated breach cost is ${Math.abs(percentDifference).toFixed(1)}% higher than the industry average of ${formatCurrency(industryAverage)}. This is likely due to factors such as data volume, detection time, or security maturity.`
    : `Your estimated breach cost is ${Math.abs(percentDifference).toFixed(1)}% lower than the industry average of ${formatCurrency(industryAverage)}. Your security controls are helping reduce potential breach costs.`;

  // Step 9: Generate recommendations
  const recommendations = generateRecommendations(
    org,
    data,
    posture,
    totalCost
  );

  // Step 10: Prevention ROI
  const comprehensiveSecurityCost = 350000; // Annual
  const fiveYearCost = comprehensiveSecurityCost * 5;
  const breachCostMultiple = totalCost / fiveYearCost;

  // Calculate cost reduction from controls
  const costReductionFromControls = Math.max(0, costWithoutControls - totalCost);

  return {
    totalCost,
    costPerRecord,
    breachLifecycleDays: posture.mttd + posture.mttc,
    costReductionFromControls,
    costWithoutControls,
    breakdown: {
      recordCost: baseRecordCost,
      detection: detectionCost,
      notification: notificationCost,
      legal: legalCost,
      fines: regulatoryFines,
      businessDisruption: businessDisruption.total,
      postBreach: postBreachCost
    },
    timeline: {
      detectionDays: posture.mttd,
      containmentDays: posture.mttc,
      totalDays: posture.mttd + posture.mttc,
      detectionCostPerDay: detectionCost / posture.mttd,
      containmentCostPerDay: (notificationCost + legalCost) / posture.mttc
    },
    insurance: {
      coverage: insuranceCoverage,
      outOfPocket,
      premiumIncrease
    },
    industryComparison: comparisonText,
    industryComparisonData: {
      industryAverage,
      percentDifference,
      globalAverage
    },
    recommendations,
    preventionROI: {
      comprehensiveSecurityCost,
      fiveYearCost,
      breachCostMultiple
    }
  };
}

// ============================================================================
// COST PER RECORD CALCULATION
// ============================================================================

function calculateCostPerRecord(
  industry: string,
  region: string,
  recordCount: number
): number {
  const baseCost = IBM_GLOBAL_AVERAGES.costPerRecord; // $165
  const industryMult = INDUSTRY_MULTIPLIERS[industry as keyof typeof INDUSTRY_MULTIPLIERS] || 1;
  const regionMult = REGION_MULTIPLIERS[region as keyof typeof REGION_MULTIPLIERS] || 1;
  const scaleFactor = getScaleFactor(recordCount);

  return baseCost * industryMult * regionMult * scaleFactor;
}

// ============================================================================
// DETECTION COSTS
// ============================================================================

function calculateDetectionCosts(mttd: number): number {
  const baseCosts = Object.values(DETECTION_COSTS).reduce((a, b) => a + b, 0);

  // Adjust based on detection time
  const multiplier = mttd > 180 ? 1.5 : mttd > 90 ? 1.2 : mttd < 30 ? 0.7 : 1.0;

  return baseCosts * multiplier;
}

// ============================================================================
// NOTIFICATION COSTS
// ============================================================================

function calculateNotificationCosts(
  recordCount: number,
  regulatedPercent: number
): number {
  const regulatedCount = Math.floor(recordCount * (regulatedPercent / 100));

  const customerNotification = regulatedCount * NOTIFICATION_COSTS.customer_notification_per_record;
  const creditMonitoring = regulatedCount * NOTIFICATION_COSTS.credit_monitoring_per_record;

  const totalCost =
    customerNotification +
    creditMonitoring +
    NOTIFICATION_COSTS.regulatory_notification +
    NOTIFICATION_COSTS.media_management +
    NOTIFICATION_COSTS.call_center_setup +
    NOTIFICATION_COSTS.website_updates;

  return totalCost;
}

// ============================================================================
// LEGAL COSTS
// ============================================================================

function calculateLegalCosts(
  industry: string,
  recordCount: number,
  dataTypes: DataType[]
): number {
  const legalCosts = {
    outside_counsel: 350000,
    regulatory_defense: 280000,
    litigation_defense: 0,
    settlement_reserve: 0
  };

  // Class action likelihood
  const hasRegulatedData = dataTypes.some(dt =>
    ['pci', 'phi', 'ssn_tax_id', 'customer_pii'].includes(dt)
  );

  if (recordCount > 50000 && hasRegulatedData) {
    legalCosts.litigation_defense = 850000;
    legalCosts.settlement_reserve = recordCount * 25; // $25 per person average
  }

  // Industry multipliers
  const industryLegalMult: Record<string, number> = {
    healthcare: 1.8,
    financial: 1.6,
    technology: 1.3,
    other: 1.0
  };

  const multiplier = industryLegalMult[industry] || 1.0;
  const total = Object.values(legalCosts).reduce((a, b) => a + b, 0);

  return total * multiplier;
}

// ============================================================================
// REGULATORY FINES
// ============================================================================

function calculateRegulatoryFines(
  industry: string,
  recordCount: number,
  dataTypes: DataType[],
  region: string,
  revenue: number
): number {
  let fines = 0;

  // GDPR (EU regions)
  if (region === 'uk' || region === 'germany' || region === 'france') {
    fines += calculateGDPRFine(revenue);
  }

  // HIPAA (Healthcare in US)
  if (industry === 'healthcare' && region === 'united_states' && dataTypes.includes('phi')) {
    fines += calculateHIPAAFine(recordCount);
  }

  // PCI DSS (Payment card data)
  if (dataTypes.includes('pci')) {
    fines += calculatePCIDSSFine(recordCount);
  }

  // State Privacy Laws (US)
  if (region === 'united_states' && recordCount > 10000) {
    fines += calculateStateFine();
  }

  // Financial regulations
  if (industry === 'financial' && region === 'united_states') {
    fines += calculateFinancialFine();
  }

  return fines;
}

// ============================================================================
// BUSINESS DISRUPTION
// ============================================================================

function calculateBusinessDisruption(
  revenue: number,
  recordCount: number,
  industry: string,
  mttc: number
): { total: number; revenueLoss: number; customerLoss: number; downtimeCost: number } {
  if (revenue === 0) {
    return { total: 0, revenueLoss: 0, customerLoss: 0, downtimeCost: 0 };
  }

  const dailyRevenue = revenue / 365;
  const disruptionPercent = getDisruptionPercentage(industry);
  const revenueLoss = dailyRevenue * mttc * disruptionPercent;

  // Customer churn
  const churnRate = recordCount > 1000000 ? 0.08 : 0.05;
  const avgCustomerLTV = (revenue / Math.max(recordCount, 1)) * 3; // 3-year LTV
  const customerLoss = recordCount * churnRate * avgCustomerLTV;

  // Downtime costs - scale with revenue
  // Small companies: ~$100-500/hour, Large companies: ~$50K-100K+/hour
  const hourlyRevenue = revenue / (365 * 24); // Revenue per hour
  const downtimeCostPerHour = Math.max(hourlyRevenue * 2, 100); // 2x hourly revenue, min $100/hr

  // More realistic downtime duration: 20% of containment time, max 72 hours (3 days)
  const hoursDown = Math.min(mttc * 0.2 * 24, 72); // Convert days to hours with 20% factor
  const downtimeCost = downtimeCostPerHour * hoursDown;

  const total = revenueLoss + customerLoss + downtimeCost;

  return {
    total,
    revenueLoss,
    customerLoss,
    downtimeCost
  };
}

// ============================================================================
// POST-BREACH COSTS
// ============================================================================

function calculatePostBreachCosts(): number {
  return Object.values(POST_BREACH_COSTS).reduce((a, b) => a + b, 0);
}

// ============================================================================
// MITIGATION FACTORS
// ============================================================================

function calculateMitigationFactor(posture: BreachSecurityPosture): number {
  let factor = 1.0;

  // Tested IR plan
  if (posture.hasIRPlan && posture.irPlanTested) {
    factor *= MITIGATION_FACTORS.tested_ir_plan; // -25%
  }

  // AI usage
  const aiReduction: Record<string, number> = {
    extensively: MITIGATION_FACTORS.ai_extensively,
    moderately: MITIGATION_FACTORS.ai_moderately,
    minimally: MITIGATION_FACTORS.ai_minimally,
    none: 1.0
  };
  factor *= aiReduction[posture.aiUsage];

  // Lifecycle time
  const totalLifecycle = posture.mttd + posture.mttc;
  if (totalLifecycle < 200) {
    factor *= MITIGATION_FACTORS.lifecycle_under_200; // -24%
  } else if (totalLifecycle > 300) {
    factor *= MITIGATION_FACTORS.lifecycle_over_300; // +23%
  }

  // Cloud environment
  if (posture.cloudEnvironment === 'hybrid') {
    factor *= MITIGATION_FACTORS.hybrid_cloud; // +15%
  }

  // Remote workforce
  if (posture.remoteWorkforcePercent > 50) {
    factor *= MITIGATION_FACTORS.remote_over_50; // +10%
  }

  // Repeat breach
  if (posture.hadRecentBreach) {
    factor *= MITIGATION_FACTORS.repeat_breach; // +40%
  }

  return factor;
}

// ============================================================================
// RECOMMENDATIONS
// ============================================================================

function generateRecommendations(
  org: BreachOrganizationProfile,
  data: BreachDataProfile,
  posture: BreachSecurityPosture,
  totalCost: number
): PreventionRecommendation[] {
  const recommendations: PreventionRecommendation[] = [];

  // High MTTD
  if (posture.mttd > 100) {
    const savings = totalCost * 0.25;
    recommendations.push({
      priority: 'CRITICAL',
      action: 'Deploy 24/7 Security Monitoring (MDR/SOC)',
      impact: `Reduce detection time from ${posture.mttd} to <30 days`,
      costSavings: savings,
      investmentNeeded: PREVENTION_EFFECTIVENESS.mdr_soc.annualCost,
      roi: (savings / PREVENTION_EFFECTIVENESS.mdr_soc.annualCost) * 100,
      category: 'detection',
      cta: {
        text: 'Explore MDR Services',
        link: '/services/mdr'
      }
    });
  }

  // No IR plan
  if (!posture.hasIRPlan || !posture.irPlanTested) {
    recommendations.push({
      priority: 'HIGH',
      action: 'Develop & Test Incident Response Plan',
      impact: 'Save $1.5M in breach response costs',
      costSavings: 1500000,
      investmentNeeded: PREVENTION_EFFECTIVENESS.tested_ir_plan.annualCost,
      roi: (1500000 / PREVENTION_EFFECTIVENESS.tested_ir_plan.annualCost) * 100,
      category: 'response',
      cta: {
        text: 'Get IR Planning Help',
        link: '/services/incident-response'
      }
    });
  }

  // No AI/automation
  if (posture.aiUsage === 'none' || posture.aiUsage === 'minimally') {
    recommendations.push({
      priority: 'HIGH',
      action: 'Implement AI-Driven Security Analytics',
      impact: 'Save $1.76M through faster detection & response',
      costSavings: 1760000,
      investmentNeeded: PREVENTION_EFFECTIVENESS.ai_security.annualCost,
      roi: (1760000 / PREVENTION_EFFECTIVENESS.ai_security.annualCost) * 100,
      category: 'detection',
      cta: {
        text: 'Explore SIEM Solutions',
        link: '/services/managed-services'
      }
    });
  }

  // Minimal maturity
  if (posture.maturityLevel === 'minimal' || posture.maturityLevel === 'basic') {
    const savings = totalCost * 0.40;
    recommendations.push({
      priority: 'CRITICAL',
      action: 'Implement Basic Security Controls (MFA, EDR, Backup)',
      impact: 'Reduce breach likelihood by 60%+',
      costSavings: savings,
      investmentNeeded: 185000,
      roi: (savings / 185000) * 100,
      category: 'prevention',
      cta: {
        text: 'Get Security Assessment',
        link: '/contact'
      }
    });
  }

  // Sort by ROI
  recommendations.sort((a, b) => b.roi - a.roi);

  return recommendations.slice(0, 3); // Top 3
}
